import React from 'react';

function decimalPlacesForStep(step) {
  if (!Number.isFinite(step) || step === 0) return 0;
  const text = String(step);
  if (text.includes('e') || text.includes('E')) {
    const fixed = step.toFixed(12).replace(/0+$/, '').replace(/\.$/, '');
    const dot = fixed.indexOf('.');
    return dot < 0 ? 0 : fixed.length - dot - 1;
  }
  const dot = text.indexOf('.');
  return dot < 0 ? 0 : text.length - dot - 1;
}

function roundToPlaces(value, places) {
  const factor = 10 ** places;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

const CustomNumericInput = ({ label, value, onChange, placeholder = '', disabled = false, min, max, step = 1, inputStep, style = {}, width = '100%', height = '58px', id, required = false, error = false, helperText = '', showStepButtons = true, labelStyle = {}, htmlRequired }) => {
  const borderColor = error ? 'var(--color-error, #d32f2f)' : 'var(--color-border-input)';
  const effectiveRequired = htmlRequired === false
    ? false
    : Boolean(required || (typeof label === 'string' && label.includes('*')));
  const handleStep = (direction) => {
    const stepNum = Number(step);
    const places = decimalPlacesForStep(stepNum);
    let newValue = value === '' || value == null || Number.isNaN(Number(value)) ? 0 : Number(value);
    if (direction === 'up') newValue += stepNum;
    if (direction === 'down') newValue -= stepNum;
    if (Number.isFinite(places)) newValue = roundToPlaces(newValue, places);
    if (min !== undefined && min !== '' && newValue < Number(min)) newValue = Number(min);
    if (max !== undefined && max !== '' && newValue > Number(max)) newValue = Number(max);
    if (Number.isFinite(places)) newValue = roundToPlaces(newValue, places);
    onChange(String(newValue));
  };

  // Use the provided height
  const inputHeight = height;
  const inputId = id || `numeric-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div style={{ width, ...style }}>
      {label && <label htmlFor={inputId} style={{ display: 'block', marginBottom: 4, color: 'var(--color-text)', fontWeight: 400, fontSize: '14px', textAlign: 'left', fontFamily: 'inherit', ...labelStyle }}>{label}</label>}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: inputHeight }}>
        <input
          id={inputId}
          type="number"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          required={effectiveRequired}
          aria-label={label || placeholder}
          aria-required={effectiveRequired}
          min={min}
          max={max}
          step={inputStep != null ? inputStep : step}
          style={{
            width: '100%',
            height: '100%',
            padding: showStepButtons ? '8px 38px 8px 16px' : '8px 16px',
            borderRadius: '8px',
            border: `1px solid ${borderColor}`,
            background: 'var(--color-bg-input)',
            color: 'var(--color-text)',
            fontSize: '15px',
            fontFamily: 'inherit',
            fontWeight: 400,
            outline: 'none',
            opacity: disabled ? 0.6 : 1,
            transition: 'border 0.2s',
            MozAppearance: 'textfield',
            maxHeight: inputHeight,
            boxSizing: 'border-box',
          }}
          onFocus={e => (e.target.style.border = error ? '1.5px solid var(--color-error, #d32f2f)' : '1.5px solid var(--color-accent)')}
          onBlur={e => (e.target.style.border = `1px solid ${borderColor}`)}
        />
        {showStepButtons && (
          <div style={{
            position: 'absolute',
            right: 4,
            top: 0,
            bottom: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 6,
            minHeight: 0,
            boxSizing: 'border-box',
          }}>
            <button
              type="button"
              tabIndex={-1}
              disabled={disabled}
              onClick={() => handleStep('up')}
              className="numeric-arrow-btn"
              style={{
                background: 'none',
                border: 'none',
                margin: 0,
                padding: 4,
                cursor: disabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: disabled ? 0.4 : 1,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                <path d="M4 10L8 6L12 10" stroke="var(--color-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              type="button"
              tabIndex={-1}
              disabled={disabled}
              onClick={() => handleStep('down')}
              className="numeric-arrow-btn"
              style={{
                background: 'none',
                border: 'none',
                margin: 0,
                padding: 4,
                cursor: disabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: disabled ? 0.4 : 1,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                <path d="M4 6L8 10L12 6" stroke="var(--color-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        )}
      </div>
      {helperText && <div style={{ marginTop: 4, fontSize: '12px', color: 'var(--color-error, #d32f2f)' }}>{helperText}</div>}
      {/* Remove browser default spinner for number input and add focus style for buttons */}
      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
        /* Show blue outline on custom buttons when focused */
        input[type=number] + div button:focus {
          outline: 2px solid #2196f3 !important;
          outline-offset: 2px;
        }
        .numeric-arrow-btn {
          min-height: 0 !important;
          min-width: 0 !important;
          height: auto !important;
          width: auto !important;
          padding: 4px !important;
          overflow: visible !important;
        }
        .numeric-arrow-btn svg {
          display: block !important;
          width: 16px !important;
          height: 16px !important;
        }
      `}</style>
    </div>
  );
};

export default CustomNumericInput; 




